import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BadgeCheck, X, Smartphone, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { verificationService } from '../../services/verificationService';

interface PersonalVerificationCardProps {
    status: 'unverified' | 'pending' | 'verified';
    onVerifySuccess: () => void;
}

type Step = 'form' | 'awaiting' | 'success' | 'error';

const POLL_INTERVAL_MS = 2_000;
const POLL_DEADLINE_MS = 5 * 60_000; // give up after 5 minutes

const isMobileUA = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const PersonalVerificationModal = ({
    isOpen,
    onClose,
    onSuccess
}: {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}) => {
    const [step, setStep] = useState<Step>('form');
    const [certName, setCertName] = useState('');
    const [certNo, setCertNo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [certifyId, setCertifyId] = useState('');
    const [certifyUrl, setCertifyUrl] = useState('');
    const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const startedAtRef = useRef<number>(0);

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setStep('form');
            setSubmitting(false);
            setErrorMsg('');
            setCertifyId('');
            setCertifyUrl('');
            stopPolling();
        }
        return stopPolling;
    }, [isOpen]);

    // Poll loop. Runs whenever we're in 'awaiting' state with a certifyId.
    useEffect(() => {
        if (step !== 'awaiting' || !certifyId) return;
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            // Deadline check
            if (Date.now() - startedAtRef.current > POLL_DEADLINE_MS) {
                if (!cancelled) {
                    setErrorMsg('认证超时，请重新发起');
                    setStep('error');
                }
                return;
            }
            try {
                const res = await verificationService.queryAlipayCertify(certifyId);
                const data: any = (res.data as any)?.data || res.data;
                if (!cancelled && data?.passed) {
                    setStep('success');
                    setTimeout(() => onSuccess(), 1500);
                    return;
                }
            } catch (err: any) {
                // Transient query errors — keep polling, log only.
                console.warn('alipay certify poll error', err);
            }
            if (!cancelled) {
                pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
            }
        };
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return () => { cancelled = true; stopPolling(); };
    }, [step, certifyId, onSuccess]);

    const handleSubmit = useCallback(async () => {
        const name = certName.trim();
        const id = certNo.trim();
        if (!name || !id) return;
        if (!/^\d{17}[\dXx]$/.test(id)) {
            setErrorMsg('请提供有效的 18 位身份证号');
            return;
        }
        setSubmitting(true);
        setErrorMsg('');
        try {
            const res = await verificationService.initAlipayCertify(name, id);
            const data: any = (res.data as any)?.data || res.data;
            if (!res.success || !data?.certifyId || !data?.certifyUrl) {
                setErrorMsg((res as any)?.error || '认证发起失败，请重试');
                setSubmitting(false);
                return;
            }
            setCertifyId(data.certifyId);
            setCertifyUrl(data.certifyUrl);
            startedAtRef.current = Date.now();
            setStep('awaiting');
            // Auto-jump to Alipay app on mobile.
            if (isMobileUA()) {
                window.location.href = data.certifyUrl;
            }
        } catch (err: any) {
            setErrorMsg(err?.response?.data?.error || err?.message || '认证发起失败，请重试');
        } finally {
            setSubmitting(false);
        }
    }, [certName, certNo]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 text-left">
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden relative shadow-xl">
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <Smartphone className="w-5 h-5" />
                        {step === 'form' ? '实名认证' : step === 'awaiting' ? '请使用支付宝完成认证' : step === 'success' ? '认证成功' : '认证失败'}
                    </h2>
                    <button onClick={onClose} disabled={step === 'success'} className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    {step === 'form' && (
                        <div className="space-y-4">
                            <div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 leading-relaxed">
                                我们将通过支付宝完成实名认证。提交后请使用支付宝扫码（电脑端）或在支付宝中继续（手机端），按提示完成人脸识别。
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">真实姓名</label>
                                <input value={certName} onChange={e => setCertName(e.target.value)} placeholder="请输入身份证上的姓名" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">身份证号</label>
                                <input value={certNo} onChange={e => setCertNo(e.target.value)} placeholder="请输入18位身份证号码" maxLength={18} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
                            </div>
                            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}
                            <button onClick={handleSubmit} disabled={submitting || !certName.trim() || !certNo.trim()} className="w-full py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
                                {submitting ? '正在发起...' : '发起认证'}
                            </button>
                            <p className="text-xs text-gray-400 text-center">您的信息仅用于身份核验，由支付宝完成识别。</p>
                        </div>
                    )}

                    {step === 'awaiting' && (
                        <div className="space-y-5 flex flex-col items-center text-center">
                            <p className="text-sm text-gray-600">
                                {isMobileUA()
                                    ? '已为您打开支付宝，请按提示完成人脸识别。如果未自动跳转，请点击下方按钮。'
                                    : '请使用手机支付宝扫描下方二维码，按提示完成人脸识别。'}
                            </p>
                            {!isMobileUA() && certifyUrl && (
                                <div className="p-4 bg-white border border-gray-200 rounded-lg">
                                    <QRCodeSVG value={certifyUrl} size={220} />
                                </div>
                            )}
                            {isMobileUA() && certifyUrl && (
                                <a href={certifyUrl} className="px-5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium">
                                    打开支付宝继续认证
                                </a>
                            )}
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                等待认证结果中...
                            </div>
                            <button onClick={() => { stopPolling(); setStep('form'); }} className="text-xs text-gray-500 hover:text-gray-800">
                                返回修改信息
                            </button>
                        </div>
                    )}

                    {step === 'success' && (
                        <div className="py-12 flex flex-col items-center justify-center">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-10 h-10 text-green-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">认证成功</h3>
                            <p className="text-gray-500 text-sm">您的实名认证已通过</p>
                        </div>
                    )}

                    {step === 'error' && (
                        <div className="py-8 flex flex-col items-center justify-center space-y-4">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-10 h-10 text-red-500" />
                            </div>
                            <p className="text-sm text-red-600 text-center">{errorMsg || '认证未通过'}</p>
                            <button onClick={() => { setStep('form'); setErrorMsg(''); }} className="px-6 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                                重新认证
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const PersonalVerificationCard: React.FC<PersonalVerificationCardProps> = ({ status, onVerifySuccess }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <div className="border border-gray-100 rounded-2xl p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                        <BadgeCheck className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            个人实名认证
                            {status === 'verified' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">已认证</span>}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-md">
                            获得蓝色个人标识 <BadgeCheck className="w-3 h-3 text-blue-500 inline align-middle" />，解锁该账号的基础接单权限与提现功能。
                        </p>
                    </div>
                </div>
                <div>
                    {status === 'verified' ? (
                        <button className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-default">已完成</button>
                    ) : (
                        <button onClick={() => setIsModalOpen(true)} className="px-5 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">去认证</button>
                    )}
                </div>
            </div>
            <PersonalVerificationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSuccess={() => { setIsModalOpen(false); onVerifySuccess(); }} />
        </div>
    );
};

export default PersonalVerificationCard;
